import { logError, logInfo } from './logger';

const DB_NAME = 'oversizehub-offline';
const DB_VERSION = 1;
let db = null;
let initPromise = null;

// Initialize IndexedDB
const initDB = () => {
  if (initPromise) return initPromise;
  if (db) return Promise.resolve(db);

  initPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      
      // Handle connection closing
      db.onclose = () => {
        db = null;
        initPromise = null;
        logInfo('Database connection closed');
      };
      
      // Handle version change
      db.onversionchange = () => {
        db.close();
        db = null;
        initPromise = null;
        logInfo('Database version changed');
      };
      
      logInfo('IndexedDB initialized successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('pendingUploads')) {
        const store = db.createObjectStore('pendingUploads', { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        store.createIndex('collection', 'collection', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        logInfo('Created pendingUploads store');
      }
    };
  });

  return initPromise;
};

// Save data for offline use
export const savePendingData = async (collectionName, data) => {
  try {
    console.log('[OfflineStorage] Saving data:', { collectionName, data });
    
    // Validate required parameters
    if (!collectionName) {
      const error = new Error('Collection name is required for offline sync');
      logError(error.message);
      return false;
    }
    if (!data) {
      const error = new Error('Data is required for offline sync');
      logError(error.message);
      return false;
    }

    // Generate unique ID for pending data
    const pendingId = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

    db = await initDB();
    if (!db) throw new Error('Database not initialized');

    const transaction = db.transaction('pendingUploads', 'readwrite');
    const store = transaction.objectStore('pendingUploads');

    return new Promise((resolve, reject) => {
      const request = store.add({
        id: pendingId,
        collection: collectionName,
        data,
        timestamp: new Date().toISOString(),
        retryCount: 0,
        status: 'pending'
      });

      transaction.oncomplete = () => {
        logInfo(`Data saved for offline sync in collection: ${collectionName}`);
        resolve(true);
      };

      transaction.onerror = () => {
        const error = transaction.error || new Error('Failed to save pending data');
        logError('Failed to save pending data:', error);
        reject(transaction.error);
      };
    });
  } catch (error) {
    const errorMessage = error?.message || 'Failed to save pending data';
    logError(errorMessage, error);
    return false;
  }
};

// Sync pending data when online
export const syncPendingData = async () => {
  if (!navigator.onLine) return false;

  let success = true;
  try {
    db = await initDB();
    if (!db) throw new Error('Database not initialized');

    const transaction = db.transaction('pendingUploads', 'readonly');
    const store = transaction.objectStore('pendingUploads');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = async () => {
        const items = request.result;
        
        for (const item of items) {
          try {
            // Process each item in a new transaction
            const writeTransaction = db.transaction('pendingUploads', 'readwrite');
            const writeStore = writeTransaction.objectStore('pendingUploads');

            await new Promise((resolve, reject) => {
              const deleteRequest = writeStore.delete(item.id);
              deleteRequest.onsuccess = () => resolve();
              deleteRequest.onerror = () => {
                success = false;
                reject(deleteRequest.error);
              };
            });

            logInfo(`Successfully synced item ${item.id}`);
          } catch (error) {
            success = false;
            logError(`Error syncing item ${item.id}:`, error);
          }
        }
        resolve(success);
      };

      request.onerror = () => {
        success = false;
        logError('Failed to get pending items:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    logError('Failed to sync pending data:', error);
    return false;
  }
};